import { axiosRubyClient } from '@/util/axios/axiosClient';
import {
  ApiResponse,
  AvailableState,
  AvailableStep,
  AuditRecord,
  BypassRequest,
  BypassResponse,
  DashboardFilters,
  DashboardResponse,
  DashboardStats,
  Pagination,
  Worker,
} from '@/types/onboardingBypass';

export class OnboardingBypassAPI {
  /**
   * Bypass worker to target state
   */
  static async bypassWorker(
    workerId: number,
    request: BypassRequest,
  ): Promise<ApiResponse<BypassResponse>> {
    const response = await axiosRubyClient.post(
      `/api/workers/${workerId}/onboarding/bypass`,
      request,
    );
    return response.data;
  }

  /**
   * Get available states for worker
   */
  static async getAvailableStates(workerId: number): Promise<
    ApiResponse<{
      worker_id: number;
      current_state: string;
      tax_type: string;
      available_states: AvailableState[];
      available_steps: AvailableStep[];
    }>
  > {
    const response = await axiosRubyClient.get(
      `/api/workers/${workerId}/onboarding/available_states`,
    );
    return response.data;
  }

  /**
   * Get worker bypass information
   */
  static async getWorkerBypassInfo(workerId: number): Promise<
    ApiResponse<{
      worker_id: number;
      has_been_bypassed: boolean;
      bypassed_at?: string;
      bypassed_by_user_id?: number;
      bypassed_by_user?: {
        id: number;
        name: string;
        email: string;
      };
      bypass_remarks?: string;
      current_onboarding_status: string;
    }>
  > {
    const response = await axiosRubyClient.get(
      `/api/workers/${workerId}/onboarding/bypass_info`,
    );
    return response.data;
  }

  /**
   * Get worker audit trail
   */
  static async getWorkerAuditTrail(
    workerId: number,
    pagination?: Partial<Pagination>,
  ): Promise<
    ApiResponse<{
      worker_id: number;
      audits: AuditRecord[];
      pagination: Pagination;
    }>
  > {
    const params = new URLSearchParams();
    if (pagination?.current_page)
      params.append('page', pagination.current_page.toString());
    if (pagination?.per_page)
      params.append('per_page', pagination.per_page.toString());

    const response = await axiosRubyClient.get(
      `/api/workers/${workerId}/onboarding/audit_trail?${params}`,
    );
    return response.data;
  }

  /**
   * Get workers dashboard
   */
  static async getDashboard(
    filters?: DashboardFilters,
    pagination?: Partial<Pagination>,
  ): Promise<ApiResponse<DashboardResponse>> {
    const params = new URLSearchParams();

    if (filters?.search) params.append('search', filters.search);
    if (filters?.onboarding_status)
      params.append('onboarding_status', filters.onboarding_status);
    if (filters?.tax_type) params.append('tax_type', filters.tax_type);
    if (filters?.agency_id)
      params.append('agency_id', filters.agency_id.toString());
    if (filters?.bypassed_only)
      params.append('bypassed_only', filters.bypassed_only.toString());
    if (filters?.sort_by) params.append('sort_by', filters.sort_by);
    if (filters?.sort_order) params.append('sort_order', filters.sort_order);

    if (pagination?.current_page)
      params.append('page', pagination.current_page.toString());
    if (pagination?.per_page)
      params.append('per_page', pagination.per_page.toString());

    const response = await axiosRubyClient.get(
      `/api/onboarding/dashboard?${params}`,
    );
    return response.data;
  }

  /**
   * Get worker details
   */
  static async getWorkerDetails(workerId: number): Promise<
    ApiResponse<{
      worker: Worker;
      audit_history: AuditRecord[];
    }>
  > {
    const response = await axiosRubyClient.get(
      `/api/onboarding/dashboard/worker/${workerId}`,
    );
    return response.data;
  }

  /**
   * Get dashboard statistics
   */
  static async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await axiosRubyClient.get(
      '/api/onboarding/dashboard/stats',
    );
    return response.data;
  }

  /**
   * Get system-wide audit history
   */
  static async getSystemAuditHistory(
    filters?: {
      worker_id?: number;
      admin_user_id?: number;
      start_date?: string;
      end_date?: string;
      new_state?: string;
    },
    pagination?: Partial<Pagination>,
  ): Promise<
    ApiResponse<{
      audits: AuditRecord[];
      pagination: Pagination;
      filters: any;
    }>
  > {
    const params = new URLSearchParams();

    if (filters?.worker_id)
      params.append('worker_id', filters.worker_id.toString());
    if (filters?.admin_user_id)
      params.append('admin_user_id', filters.admin_user_id.toString());
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.new_state) params.append('new_state', filters.new_state);

    if (pagination?.current_page)
      params.append('page', pagination.current_page.toString());
    if (pagination?.per_page)
      params.append('per_page', pagination.per_page.toString());

    const response = await axiosRubyClient.get(
      `/api/onboarding/dashboard/audit?${params}`,
    );
    return response.data;
  }
}
